<script setup>
/* Klubregister (nur admin): alle Vereine mit Name und Logo pflegen.
 *
 * Liste mit Suche, Logo, Name, Zahl der Benutzungen (Spieler, Transfers) und der Markierung "Logo fehlt".
 * Aktionen: anlegen (Name + Logo, beides Pflicht), Name ändern, Logo tauschen, zusammenführen
 * (ClubMergeDialog.vue), löschen (nur bei null Benutzungen). Dazu die einmalige Aktion "Logos ins Register
 * kopieren" (Kopie, nie Verschieben, wiederholbar; domain/clubLogoCopy.js).
 *
 * Es wird nie eine Logo-Datei gelöscht: Ein ersetztes oder zusammengeführtes Logo bleibt im Speicher liegen.
 * Meldungen laufen über AppSnackbar (useSnackbar); Fehlertexte sind allgemein (errors.*), Einzelheiten der
 * Datenbank stehen nur in der Konsole. Den Dialog gibt es nur für admin (AccountMenu.vue); die Datenbank
 * erzwingt es ohnehin (Zeilenregeln, Funktionen).
 */
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import OverlayModal from './OverlayModal.vue';
import ClubLogo from './ClubLogo.vue';
import ClubMergeDialog from './ClubMergeDialog.vue';
import { useFootballClubs } from '../composables/useFootballClubs.js';
import { useSnackbar } from '../composables/useSnackbar.js';
import { useConfirm } from '../composables/useConfirm.js';
import { filterClubs, lacksLogo, canDeleteClub, usageTotal, validateClubName } from '../domain/footballClub.js';
import { clubsNeedingCopy } from '../domain/clubLogoCopy.js';

const open = defineModel({ type: Boolean, default: false });

const { t, te } = useI18n();
const { show } = useSnackbar();
const { confirm } = useConfirm();
const {
  clubs, sorted, loaded, loading, error, usage, usageLoaded, lackingLogo,
  ensureLoaded, loadUsage, addClub, renameClub, changeLogo, removeClub, copyLogos,
} = useFootballClubs();

const query = ref('');
const onlyMissing = ref(false);
const busy = ref(false);
/* Vor dem watch auf `open` deklariert: Dessen erster Lauf (immediate) setzt sie zurück */
const adding = ref(false);
const editingId = ref(null);

const rows = computed(() => {
  const found = filterClubs(sorted.value, query.value);
  return onlyMissing.value ? found.filter(lacksLogo) : found;
});

/* Fehlertext: allgemeiner Schlüssel unter errors.*, sonst "konnte nicht gespeichert werden" */
const errText = (e) => (e && te(`errors.${e.message}`) ? t(`errors.${e.message}`) : t('errors.saveFailed'));

watch(open, async (isOpen) => {
  if (!isOpen) return;
  query.value = '';
  onlyMissing.value = false;
  adding.value = false;
  editingId.value = null;
  await ensureLoaded({ force: true });
  try {
    await loadUsage();
  } catch (e) {
    console.error('[klubregister] Nutzungszahlen:', e && e.message);
  }
}, { immediate: true });

/* ---------- Anlegen ---------- */
const newName = ref('');
const newFile = ref(null);
const newFileInput = ref(null);

const newCheck = computed(() => validateClubName(newName.value, clubs.value));
const canCreate = computed(() => newCheck.value.ok && !!newFile.value && !busy.value);

function startAdd() {
  adding.value = true;
  newName.value = '';
  newFile.value = null;
}
function onPickNew(event) {
  newFile.value = event.target.files?.[0] || null;
  event.target.value = '';
}
async function submitAdd() {
  if (!canCreate.value) return;
  busy.value = true;
  try {
    const club = await addClub({ name: newName.value, file: newFile.value });
    show(t('clubRegister.created', { name: club.name }));
    adding.value = false;
  } catch (e) {
    show(errText(e), 'error');
  } finally {
    busy.value = false;
  }
}

/* ---------- Name ändern ---------- */
const editName = ref('');
const editCheck = computed(() => validateClubName(editName.value, clubs.value, editingId.value));

function startEdit(club) {
  editingId.value = club.id;
  editName.value = club.name;
}
async function submitEdit() {
  if (!editCheck.value.ok || busy.value) return;
  busy.value = true;
  try {
    await renameClub(editingId.value, editName.value);
    show(t('clubRegister.saved'));
    editingId.value = null;
  } catch (e) {
    show(errText(e), 'error');
  } finally {
    busy.value = false;
  }
}

/* ---------- Logo tauschen ---------- */
const logoInput = ref(null);
let logoTarget = null;
function pickLogo(club) {
  logoTarget = club.id;
  logoInput.value.click();
}
async function onPickLogo(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file || !logoTarget) return;
  busy.value = true;
  try {
    await changeLogo(logoTarget, file);
    show(t('clubRegister.logoSaved'));
  } catch (e) {
    show(errText(e), 'error');
  } finally {
    busy.value = false;
  }
}

/* ---------- Löschen: nur ohne Benutzung ---------- */
async function onDelete(club) {
  if (!canDeleteClub(usage.value.get(club.id))) return;
  const accepted = await confirm({
    title: t('clubRegister.deleteTitle'),
    message: t('clubRegister.deleteMessage', { name: club.name }),
  });
  if (!accepted) return;
  busy.value = true;
  try {
    await removeClub(club.id);
    show(t('clubRegister.deleted', { name: club.name }));
  } catch (e) {
    show(errText(e), 'error');
  } finally {
    busy.value = false;
  }
}

/* ---------- Zusammenführen ---------- */
const mergeSource = ref(null);

/* ---------- Logos ins Register kopieren ---------- */
const copy = ref({ running: false, total: 0, done: 0, failed: 0, finished: false });
const toCopy = computed(() => clubsNeedingCopy(clubs.value).length);
let stopRequested = false;

async function runCopy() {
  stopRequested = false;
  copy.value = { running: true, total: toCopy.value, done: 0, failed: 0, finished: false };
  try {
    const result = await copyLogos({
      onProgress: (p) => { copy.value = { ...copy.value, ...p, running: true }; },
      shouldStop: () => stopRequested,
    });
    copy.value = { ...copy.value, running: false, finished: true, total: result.total, failed: result.failed.length };
    show(
      result.failed.length ? t('clubRegister.copyDoneWithErrors', { count: result.failed.length }) : t('clubRegister.copyDone'),
      result.failed.length ? 'warning' : 'success'
    );
  } catch (e) {
    copy.value = { ...copy.value, running: false };
    show(errText(e), 'error');
  }
}

const usageText = (club) => {
  const u = usage.value.get(club.id);
  if (!u) return '';
  return usageTotal(u) === 0
    ? t('clubRegister.unused')
    : t('clubRegister.usage', { players: u.players, transfers: u.transfers });
};

function close() {
  if (!busy.value && !copy.value.running) open.value = false;
}
</script>

<template>
  <OverlayModal v-if="open" :title="$t('clubRegister.title')" :width="640" @close="close">
    <p class="cr-intro">{{ $t('clubRegister.intro') }}</p>

    <p v-if="loading && !loaded" class="cr-note">{{ $t('common.loading') }}</p>
    <p v-else-if="error && !loaded" class="cr-note cr-bad" role="alert">{{ $t('errors.loadFailed') }}</p>

    <template v-else>
      <p class="cr-stats">
        {{ $t('clubRegister.count', { count: clubs.length }) }}
        <span v-if="lackingLogo" class="cr-missing">· {{ $t('clubRegister.lackingCount', { count: lackingLogo }) }}</span>
      </p>

      <div class="cr-tools">
        <input v-model="query" type="text" class="cr-search" :placeholder="$t('clubRegister.search')" :aria-label="$t('clubRegister.search')" />
        <label class="cr-check">
          <input v-model="onlyMissing" type="checkbox" /> {{ $t('clubRegister.onlyMissing') }}
        </label>
        <button type="button" class="btn small" :disabled="busy" @click="startAdd">{{ $t('clubRegister.add') }}</button>
      </div>

      <!-- Anlegen -->
      <div v-if="adding" class="cr-form">
        <div class="field">
          <label>{{ $t('clubRegister.name') }} *</label>
          <input v-model="newName" type="text" maxlength="80" @keydown.enter.prevent="submitAdd" />
        </div>
        <div class="cr-logo-pick">
          <button type="button" class="btn ghost small" :disabled="busy" @click="newFileInput.click()">
            {{ newFile ? $t('clubRegister.logoReplace') : $t('clubRegister.logoChoose') }}
          </button>
          <span class="cr-file">{{ newFile ? newFile.name : $t('clubRegister.logoRequired') }}</span>
          <input ref="newFileInput" type="file" accept="image/*" hidden @change="onPickNew" />
        </div>
        <p v-if="newName && !newCheck.ok" class="cr-bad">
          {{ $t(`clubRegister.validation.${newCheck.errors[0]}`, { name: newCheck.duplicate && newCheck.duplicate.name, max: 80 }) }}
        </p>
        <div class="cr-actions">
          <button type="button" class="btn ghost small" :disabled="busy" @click="adding = false">{{ $t('common.cancel') }}</button>
          <button type="button" class="btn small" :disabled="!canCreate" @click="submitAdd">
            {{ busy ? $t('common.loading') : $t('common.save') }}
          </button>
        </div>
      </div>

      <!-- Liste -->
      <ul class="cr-list">
        <li v-for="club in rows" :key="club.id" class="cr-row">
          <ClubLogo :path="club.logoPath" :size="36" />
          <div class="cr-main">
            <template v-if="editingId === club.id">
              <input v-model="editName" type="text" maxlength="80" class="cr-edit" @keydown.enter.prevent="submitEdit" @keydown.esc="editingId = null" />
              <p v-if="!editCheck.ok" class="cr-bad">
                {{ $t(`clubRegister.validation.${editCheck.errors[0]}`, { name: editCheck.duplicate && editCheck.duplicate.name, max: 80 }) }}
              </p>
            </template>
            <template v-else>
              <span class="cr-name">{{ club.name }}</span>
              <span v-if="lacksLogo(club)" class="cr-chip">{{ $t('clubRegister.logoMissing') }}</span>
              <span v-if="usageLoaded" class="cr-usage">{{ usageText(club) }}</span>
            </template>
          </div>
          <div class="cr-row-actions">
            <template v-if="editingId === club.id">
              <button type="button" class="th-act" :disabled="busy || !editCheck.ok" @click="submitEdit">{{ $t('common.save') }}</button>
              <button type="button" class="th-act" @click="editingId = null">{{ $t('common.cancel') }}</button>
            </template>
            <template v-else>
              <button type="button" class="th-act" :disabled="busy" @click="startEdit(club)">{{ $t('clubRegister.rename') }}</button>
              <button type="button" class="th-act" :disabled="busy" @click="pickLogo(club)">{{ $t('clubRegister.logoChange') }}</button>
              <button type="button" class="th-act" :disabled="busy || clubs.length < 2" @click="mergeSource = club">{{ $t('clubRegister.merge') }}</button>
              <button
                type="button"
                class="th-act danger"
                :disabled="busy || !canDeleteClub(usage.get(club.id))"
                :title="canDeleteClub(usage.get(club.id)) ? '' : $t('clubRegister.deleteBlocked')"
                @click="onDelete(club)"
              >{{ $t('common.delete') }}</button>
            </template>
          </div>
        </li>
        <li v-if="!rows.length" class="cr-note">{{ $t('clubRegister.empty') }}</li>
      </ul>
      <input ref="logoInput" type="file" accept="image/*" hidden @change="onPickLogo" />

      <!-- Einmalige Aktion: Logos ins Register kopieren -->
      <section v-if="toCopy > 0 || copy.finished" class="cr-copy">
        <h3>{{ $t('clubRegister.copyTitle') }}</h3>
        <p class="cr-note">{{ $t('clubRegister.copyIntro', { count: toCopy }) }}</p>
        <div v-if="copy.running || copy.finished" class="cr-progress" role="status">
          {{ $t('clubRegister.copyProgress', { done: copy.done, total: copy.total, failed: copy.failed }) }}
        </div>
        <div class="cr-actions">
          <button v-if="copy.running" type="button" class="btn ghost small" @click="stopRequested = true">{{ $t('clubRegister.copyStop') }}</button>
          <button v-else type="button" class="btn small" :disabled="busy || toCopy === 0" @click="runCopy">
            {{ copy.finished && toCopy ? $t('clubRegister.copyAgain') : $t('clubRegister.copyStart') }}
          </button>
        </div>
      </section>
    </template>

    <ClubMergeDialog v-if="mergeSource" :source="mergeSource" @close="mergeSource = null" />

    <template #actions>
      <button class="btn small" :disabled="busy || copy.running" @click="close">{{ $t('common.close') }}</button>
    </template>
  </OverlayModal>
</template>

<style scoped>
.cr-intro, .cr-note { margin: 0 0 10px; font-size: var(--fs-sm); color: var(--muted); }
.cr-stats { margin: 0 0 10px; font-size: var(--fs-sm); color: var(--text); }
.cr-missing { color: var(--amber); }
.cr-bad { margin: 4px 0 0; font-size: var(--fs-xs); color: var(--red); }
.cr-tools { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; margin-bottom: 12px; }
.cr-search {
  flex: 1 1 12rem;
  min-width: 0;
  padding: 7px 9px;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text);
  font: inherit;
  font-size: var(--fs-sm);
}
.cr-check { display: flex; align-items: center; gap: 6px; font-size: var(--fs-xs); color: var(--muted); }
.cr-form { margin-bottom: 14px; padding: 12px; border: 1px solid var(--border); border-radius: 10px; }
.cr-logo-pick { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 8px; }
.cr-file { font-size: var(--fs-xs); color: var(--muted); overflow-wrap: anywhere; }
.cr-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 8px; }
.cr-list { list-style: none; margin: 0; padding: 0; max-height: 52vh; overflow-y: auto; }
.cr-row { display: flex; align-items: center; gap: 12px; padding: 8px 0; border-bottom: 1px solid var(--border-soft); }
.cr-main { flex: 1; min-width: 0; display: flex; flex-wrap: wrap; align-items: center; gap: 4px 10px; }
.cr-name { font-weight: 600; overflow-wrap: anywhere; }
.cr-chip { font-size: var(--fs-2xs); padding: 1px 7px; border: 1px solid var(--amber); border-radius: 999px; color: var(--amber); }
.cr-usage { font-size: var(--fs-xs); color: var(--dim); }
.cr-edit {
  flex: 1 1 10rem;
  min-width: 0;
  padding: 6px 8px;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text);
  font: inherit;
}
.cr-row-actions { display: flex; gap: 4px; flex-wrap: wrap; justify-content: flex-end; }
.cr-copy { margin-top: 16px; padding-top: 12px; border-top: 1px solid var(--border); }
.cr-copy h3 { margin: 0 0 6px; font-size: var(--fs-sm); color: var(--text); }
.cr-progress { margin-top: 6px; font-size: var(--fs-xs); color: var(--green); }
@media (max-width: 560px) {
  .cr-row { flex-wrap: wrap; }
  .cr-row-actions { width: 100%; justify-content: flex-start; }
}
</style>
